/**
 * Brand members + invite endpoints.
 *
 * Member management:
 *   listMembers, getMember, inviteMember, getAccess, updateAccess, changeRole,
 *   suspendMember, resumeMember, removeMember
 *
 * Invite management:
 *   listInvites, resendInvite, revokeInvite
 *
 * Public (no auth):
 *   verifyInvite, acceptInvite, verifyActivation, setActivationPassword
 */
import { apiClient } from '@discovr/common';
import type { PaginatedResponse, DataResponse } from '@discovr/common';
import type { GrantLevel } from './campaignGrants';

/** A campaign-grant to apply as part of an invite (before the invitee exists as a member). */
export interface PendingGrant {
  campaign_id: string;
  level: GrantLevel;
}

/** A single campaign's access grant, hydrated with campaign name for display. */
export interface MemberCampaignGrant {
  campaign_id: string;
  campaign_name: string;
  level: GrantLevel;
  granted_at: string;
}

/** A member's full campaign access — `all_campaigns: true` covers owner/admin-tier
 *  unrestricted access, so `grants` is only meaningful (and only sent by the
 *  backend) when it's false. Discriminated on `all_campaigns` so a caller can't
 *  represent (or need to handle) `all_campaigns: true` with a non-empty `grants`. */
export type MemberAccess =
  | { all_campaigns: true }
  | { all_campaigns: false; grants: MemberCampaignGrant[] };

/** Roles assignable via `changeRole` — 'brand_owner' is never a target of a
 *  plain role change (ownership transfer isn't a role-change flow). */
export type BrandMemberRole = 'brand_admin' | 'brand_member';

export interface BrandMember {
  id: string;
  brand_id: string;
  user_id: string;
  email: string;
  display_name?: string;
  role: 'brand_owner' | 'brand_admin' | 'brand_member';
  status: 'active' | 'suspended' | 'inactive';
  accepted_at: string;
  created_at: string;
  updated_at: string;
}

export interface BrandInvite {
  id: string;
  brand_id: string;
  email: string;
  role: 'brand_admin' | 'brand_member';
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  created_at: string;
  expires_at: string;
}

export interface VerifyInviteResponse {
  email: string;
  brand_name: string;
  role: string;
  [key: string]: unknown;
}

export interface AcceptInviteResponse {
  firebase_custom_token: string;
  user: unknown;
}

export interface VerifyActivationResponse {
  email: string;
  brand_name: string;
  [key: string]: unknown;
}

export interface SetActivationPasswordResponse {
  status: string;
  firebase_custom_token: string;
}

export const members = {
  // ─── Public (no auth) ─────────────────────────────────────────────────────

  /** Verify a brand invite token (public, from email link). */
  verifyInvite: (token: string): Promise<VerifyInviteResponse> => {
    const params = new URLSearchParams({ token });
    return apiClient
      .get<DataResponse<VerifyInviteResponse>>(`/invites/verify?${params}`)
      .then((r) => r.data.data);
  },

  /** Accept a brand invite and set password (public, from email link). */
  acceptInvite: (body: {
    token: string;
    password: string;
    first_name: string;
    last_name: string;
  }): Promise<AcceptInviteResponse> =>
    apiClient
      .post<DataResponse<AcceptInviteResponse>>('/invites/accept', body)
      .then((r) => r.data.data),

  /** Accept a brand invite via a Google sign-in the FE already completed client-side (public). */
  acceptInviteGoogle: (body: { token: string; id_token: string }): Promise<AcceptInviteResponse> =>
    apiClient
      .post<DataResponse<AcceptInviteResponse>>('/invites/accept-google', body)
      .then((r) => r.data.data),

  /** Verify a brand-owner activation token (public, from the approval email link). */
  verifyActivation: (token: string): Promise<VerifyActivationResponse> => {
    const params = new URLSearchParams({ token });
    return apiClient
      .get<DataResponse<VerifyActivationResponse>>(`/activations/verify?${params}`)
      .then((r) => r.data.data);
  },

  /** Set a brand owner's password via activation token (public, from email link).
   *  Returns a Firebase custom token so the FE can sign the owner in immediately. */
  setActivationPassword: (body: { token: string; password: string }): Promise<SetActivationPasswordResponse> =>
    apiClient
      .post<DataResponse<SetActivationPasswordResponse>>('/activations/set-password', body)
      .then((r) => r.data.data),

  // ─── Authenticated ─────────────────────────────────────────────────────────

  /** List brand members (paginated). */
  listMembers: (params?: { page?: number; limit?: number }): Promise<PaginatedResponse<BrandMember>> =>
    apiClient
      .get<PaginatedResponse<BrandMember>>('/members', { params })
      .then((r) => r.data),

  /** Get a single brand member by ID. */
  getMember: (id: string): Promise<BrandMember> =>
    apiClient
      .get<DataResponse<BrandMember>>(`/members/${encodeURIComponent(id)}`)
      .then((r) => r.data.data),

  /** Invite a new brand member, optionally granting campaign access up front. */
  inviteMember: (body: { email: string; role: string; campaign_grants?: PendingGrant[] }): Promise<BrandInvite> =>
    apiClient
      .post<DataResponse<BrandInvite>>('/members', body)
      .then((r) => r.data.data),

  /** Get a member's campaign access grants. */
  getAccess: (id: string): Promise<MemberAccess> =>
    apiClient
      .get<DataResponse<MemberAccess>>(`/members/${encodeURIComponent(id)}/access`)
      .then((r) => r.data.data),

  /** Upsert and/or revoke a member's campaign access grants in one call. */
  updateAccess: (id: string, body: { upsert?: PendingGrant[]; revoke?: string[] }): Promise<MemberAccess> =>
    apiClient
      .put<DataResponse<MemberAccess>>(`/members/${encodeURIComponent(id)}/access`, body)
      .then((r) => r.data.data),

  /** Change a member's role. WriteNoContent → 204. */
  changeRole: (id: string, role: BrandMemberRole): Promise<void> =>
    apiClient
      .post(`/members/${encodeURIComponent(id)}/role`, { role })
      .then(() => undefined),

  /** Suspend a brand member. WriteNoContent → 204. */
  suspendMember: (id: string): Promise<void> =>
    apiClient
      .post(`/members/${encodeURIComponent(id)}/suspend`)
      .then(() => undefined),

  /** Resume a suspended brand member. WriteNoContent → 204. */
  resumeMember: (id: string): Promise<void> =>
    apiClient
      .post(`/members/${encodeURIComponent(id)}/resume`)
      .then(() => undefined),

  /** Remove a brand member. WriteNoContent → 204. */
  removeMember: (id: string): Promise<void> =>
    apiClient
      .delete(`/members/${encodeURIComponent(id)}`)
      .then(() => undefined),

  /** List brand invites (paginated). */
  listInvites: (params?: { page?: number; limit?: number }): Promise<PaginatedResponse<BrandInvite>> =>
    apiClient
      .get<PaginatedResponse<BrandInvite>>('/invites', { params })
      .then((r) => r.data),

  /** Resend a pending invite. WriteNoContent → 204. */
  resendInvite: (id: string): Promise<void> =>
    apiClient
      .post(`/invites/${encodeURIComponent(id)}/resend`)
      .then(() => undefined),

  /**
   * Revoke a pending invite. WriteNoContent → 204.
   * `body.confirm` carries the typed-confirmation value (ActionConfirmDialog's
   * `confirmText="CONFIRM"` gate) for the BE's confirm.Matches check. DELETE
   * has no body param on axios's shorthand, so it goes through `config.data`.
   */
  revokeInvite: (id: string, body?: { confirm?: string }): Promise<void> =>
    apiClient
      .delete(`/invites/${encodeURIComponent(id)}`, { data: body })
      .then(() => undefined),
};
